import { useState, useEffect } from 'react';
import { ChevronRight, Search, ChevronsLeft, ChevronsRight, ChevronLeft } from 'lucide-react';
import { useGetUserList, useUpdateUserStatus } from "../../../hooks/useUsers"
import { useQueryClient } from '@tanstack/react-query';
import moment from "moment"
import toast from 'react-hot-toast';

const AllUsers = () => {
  const [userList, setUserList] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { data: userListResponse, isSuccess: isUserListSuccess } = useGetUserList(`?page=${currentPage}&limit=${rowsPerPage}&search_key=${searchTerm}`)
  const { REACT_APP_IMG_URL } = process.env
  const updateStatusMutation = useUpdateUserStatus();
  const queryClient = useQueryClient();

  const toggleStatus = (id, currentStatus) => {
    updateStatusMutation.mutate({
      status: currentStatus == 1 ? 3 : 1,
      user_id: id
    }, {
      onSuccess: () => {
        toast.success("User status updated.")
        queryClient.invalidateQueries({ queryKey: ['users'] });
      },
      onError: (error) => {
        const message = error.response?.data?.message
        toast.error(message)
      }
    })
  };

  useEffect(() => {
    if (isUserListSuccess) {
      setUserList(userListResponse?.data?.data?.result)
      let tp = Math.ceil((userListResponse?.data?.data?.totalCount) / rowsPerPage)
      setTotalPages(tp == 0 ? 1 : tp)
    }
  }, [userListResponse, isUserListSuccess])

  return (
    <div className="flex min-h-screen bg-gray-50 mb-14">
      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Breadcrumb */}
        <div className="bg-white border-b border-gray-200 px-8 py-4">
          <div className="flex items-center space-x-2 text-sm text-gray-500">
            <span>Dashboard</span>
            <ChevronRight className="w-4 h-4" />
            <span>Users</span>
            <ChevronRight className="w-4 h-4" />
            <span className="text-gray-900">All Users</span>
          </div>
        </div>
        {/* Page Content */}
        <div className="flex-1 px-1 py-4">
          <div className="max-w-7xl">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">All Users ({userListResponse?.data?.data?.totalCount})</h1>
                <p className="text-gray-600">All the users are listed here.</p>
              </div>
            </div>
            {/* Search */}
            <div className="mb-6">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search users by name or username"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            {/* Table */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Full Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nick Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Joined on
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Gender
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Profile Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {userList?.map((user) => (
                    <tr key={user?._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          <img
                            src={`${REACT_APP_IMG_URL}/${user?.profile_pic}`}
                            alt={`${user?.first_name} ${user?.last_name}`}
                            className="w-8 h-8 rounded-full object-cover"
                          />
                          <span className="text-sm font-medium text-gray-900">{`${user?.first_name} ${user?.last_name}`}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{user?.nick_name}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{moment(user?.createdAt).format("lll")}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{user?.gender?`${user?.gender[0]?.toUpperCase()}${user?.gender.substr(1, user?.gender?.length)?.toLowerCase()}`:"N/A"}</td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${user?.status === 1
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                          }`}>
                          {user.status == 1 ? "Active" : "Disabled"}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{user?.email != "" ? user?.email : "N/A"}</td>
                      <td className="px-4 py-4 text-center">
                        <button
                          onClick={() => toggleStatus(user?._id, user?.status)}
                          className="focus:outline-none">
                          <div className={`w-12 h-6 rounded-full flex items-center ${user?.status == 1 ? 'bg-gray-900' : 'bg-gray-300'} p-1 transition-colors duration-200`}>
                            <div className={`w-4 h-4 rounded-full bg-white transform transition-transform duration-200 ${user.status == 1 ? 'translate-x-6' : 'translate-x-0'}`}></div>
                          </div>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {/* Pagination */}
              <div className="flex justify-end m-4">
                <div className="flex items-center">
                  <div className="mr-4 flex justify-between">
                    <span className="mt-1 mr-2 text-sm text-gray-500">Rows per page</span>
                    <select
                      className="w-[55px] border border-gray-300 rounded p-1 text-sm"
                      value={rowsPerPage}
                      onChange={(e) => { setCurrentPage(1); setRowsPerPage(Number(e.target.value)) }}>
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </select>
                  </div>
                  <div className="flex items-center text-sm">
                    <span className="mr-4">Page {currentPage} of {totalPages}</span>
                    <div className="flex">
                      <button
                        className="p-1 border border-gray-300 rounded-l hover:bg-gray-100"
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}>
                        <ChevronsLeft size={16} />
                      </button>
                      <button
                        className="p-1 border-t border-b border-gray-300 hover:bg-gray-100"
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}>
                        <ChevronLeft size={16} />
                      </button>
                      <button
                        className="p-1 border-t border-b border-gray-300 hover:bg-gray-100"
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}>
                        <ChevronRight size={16} />
                      </button>
                      <button
                        className="p-1 border border-gray-300 rounded-r hover:bg-gray-100"
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}>
                        <ChevronsRight size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllUsers;